import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { PauseCircle, PlayCircle } from 'lucide-react';
import { api } from '../services/api';
import { toast } from '../store/toastStore';
import { apiErrorMessage } from './sales/shared';

/**
 * Decision 74 — "waiting for customer" stops the SLA clock (history kept);
 * resuming re-applies the remaining business time. Customer activity resumes
 * it automatically on the server.
 *
 * The same state and actions as a hook (T-44): ticket detail moves the
 * pause / resume action into its ⋯ menu and keeps the paused state visible in
 * the SLA block. `available` is false until the SLA state has loaded.
 */
export function useSlaPause(ticketId: string, enabled = true) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryKey: ['ticket-sla', ticketId],
    queryFn: () => api.getTicketSla(ticketId).then((r) => r.data),
    retry: false,
    enabled,
  });
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['ticket-sla', ticketId] });
    queryClient.invalidateQueries({ queryKey: ['ticket', ticketId] });
  };
  const pause = useMutation({
    mutationFn: () => api.pauseTicketSla(ticketId, t('slaPause.defaultReason')),
    onSuccess: invalidate,
    onError: (err) => toast('error', apiErrorMessage(err, t('common.actionFailed'))),
  });
  const resume = useMutation({
    mutationFn: () => api.resumeTicketSla(ticketId),
    onSuccess: invalidate,
    onError: (err) => toast('error', apiErrorMessage(err, t('common.actionFailed'))),
  });
  const paused = !!data?.sla_paused_at;
  return {
    available: !!data,
    paused,
    pending: pause.isPending || resume.isPending,
    toggle: () => (paused ? resume.mutate() : pause.mutate()),
  };
}

export default function SlaPauseToggle({ ticketId }: { ticketId: string }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryKey: ['ticket-sla', ticketId],
    queryFn: () => api.getTicketSla(ticketId).then((r) => r.data),
    retry: false,
  });
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['ticket-sla', ticketId] });
    queryClient.invalidateQueries({ queryKey: ['ticket', ticketId] });
  };
  const pause = useMutation({
    mutationFn: () => api.pauseTicketSla(ticketId, t('slaPause.defaultReason')),
    onSuccess: invalidate,
    onError: (err) => toast('error', apiErrorMessage(err, t('common.actionFailed'))),
  });
  const resume = useMutation({
    mutationFn: () => api.resumeTicketSla(ticketId),
    onSuccess: invalidate,
    onError: (err) => toast('error', apiErrorMessage(err, t('common.actionFailed'))),
  });
  if (!data) return null;
  const paused = !!data.sla_paused_at;
  return (
    <button
      type="button"
      onClick={() => (paused ? resume.mutate() : pause.mutate())}
      disabled={pause.isPending || resume.isPending}
      className={`inline-flex items-center gap-1.5 rounded-lg px-2 py-1 -ms-2 text-xs font-medium transition-colors duration-150 disabled:opacity-50 ${paused ? 'text-warning-800 bg-warning-50 hover:bg-warning-100' : 'text-surface-600 hover:text-surface-900 hover:bg-surface-100'}`}
      title={paused ? t('slaPause.resumeHint') : t('slaPause.pauseHint')}
    >
      {paused ? <PlayCircle size={14} aria-hidden="true" /> : <PauseCircle size={14} aria-hidden="true" />}
      {paused ? t('slaPause.paused') : t('slaPause.waitingForCustomer')}
    </button>
  );
}
